<script setup lang="ts">
  import { computed, useAttrs, useId, useSlots } from 'vue';
  import type { ButtonProps } from './Button.types';
  import {
    ButtonColor,
    ButtonIconPosition,
    ButtonSize,
    ButtonType,
    ButtonVariant,
    type ButtonVariantType,
  } from './Button.types';
  import FontIcon from '../FontIcon/FontIcon.vue';
  import { Loader, LoaderSize } from '../Loader';
  import { Tooltip, TooltipPosition } from '../Tooltip';
  import {
    baseClass,
    colorClassesByVariant,
    disabledTooltipButtonClass,
    disabledTooltipTriggerClass,
    iconClass,
    iconEndClass,
    iconGhostClass,
    iconSizeClasses,
    smSizeClass,
    textLikeLabelClass,
    variantClasses,
  } from './Button.css';

  defineOptions({
    inheritAttrs: false,
  });

  const attrs = useAttrs();
  const slots = useSlots();
  const props = defineProps<ButtonProps>();

  const uid = useId();
  const resolvedTestId = computed(() => props.testId ?? `ds-button-${uid}`);

  const emit = defineEmits<{
    click: [event: MouseEvent];
  }>();

  const variant = computed((): ButtonVariantType => {
    const v = props.variant;
    return v ?? ButtonVariant.Primary;
  });
  const buttonType = computed(() => props.type ?? ButtonType.Button);
  const isIconOnlyButton = computed(() => variant.value === ButtonVariant.Icon);
  const isTextLike = computed(
    () =>
      variant.value === ButtonVariant.Text ||
      variant.value === ButtonVariant.Inline,
  );

  const resolvedColor = computed(
    () =>
      props.color ??
      (isTextLike.value ? ButtonColor.Secondary : ButtonColor.Default),
  );

  const colorModifierClass = computed(
    (): string | null =>
      colorClassesByVariant[variant.value][resolvedColor.value] ?? null,
  );

  const hasIconName = computed(() => !!props.iconName);
  const iconClasses = computed(() => [
    iconClass,
    props.iconPosition === ButtonIconPosition.End ? iconEndClass : null,
  ]);
  const size = computed(() => props.size ?? ButtonSize.MD);
  const hasSmSize = computed(
    () =>
      size.value === ButtonSize.SM &&
      !isIconOnlyButton.value &&
      variant.value !== ButtonVariant.Inline,
  );

  const isDisabled = computed(() => !!props.disabled);
  const isLoading = computed(() => !!props.loading);
  const isLoadingOnly = computed(() => isLoading.value && !isDisabled.value);
  const isInteractionBlocked = computed(
    () => isDisabled.value || isLoading.value,
  );
  const showDisabledTooltip = computed(
    () => isDisabled.value && !!props.disabledReason,
  );

  const classes = computed(() => [
    baseClass,
    variantClasses[variant.value],
    isIconOnlyButton.value ? iconSizeClasses[size.value] : null,
    hasSmSize.value ? smSizeClass : null,
    colorModifierClass.value,
    isIconOnlyButton.value && props.isGhost ? iconGhostClass : null,
    showDisabledTooltip.value ? disabledTooltipButtonClass : null,
  ]);

  const buttonAttrs = computed(() => ({
    ...attrs,
    class: [attrs.class, classes.value],
    disabled: isDisabled.value || undefined,
    'aria-disabled': isLoadingOnly.value || undefined,
    'aria-busy': isLoading.value || undefined,
    'aria-label': props.ariaLabel,
    'data-active': props.active || undefined,
    'data-testid': resolvedTestId.value,
  }));

  const onClick = (event: MouseEvent) => {
    if (!isInteractionBlocked.value) {
      emit('click', event);
      return;
    }

    event.preventDefault();
    event.stopPropagation();
  };

  const activationKeys = new Set(['Enter', ' ', 'Spacebar']);

  const onKeydown = (event: KeyboardEvent) => {
    if (isInteractionBlocked.value && activationKeys.has(event.key)) {
      event.preventDefault();
      event.stopPropagation();
    }
  };
</script>

<template>
  <Tooltip
    v-if="showDisabledTooltip"
    :interactive="true"
    :position="TooltipPosition.TOP"
    :description="disabledReason"
  >
    <span :class="disabledTooltipTriggerClass">
      <button
        v-bind="buttonAttrs"
        :type="buttonType"
        @click="onClick"
        @keydown="onKeydown"
      >
        <template v-if="isIconOnlyButton">
          <Loader v-if="isLoading" :size="LoaderSize.SM" />
          <FontIcon v-else-if="hasIconName" :icon-name="props.iconName!" />
        </template>
        <template v-else-if="isLoading">
          <Loader :size="LoaderSize.SM" />
        </template>
        <template v-else>
          <span v-if="hasIconName || slots.icon" :class="iconClasses">
            <FontIcon v-if="hasIconName" :icon-name="props.iconName!" />
            <slot v-else-if="slots.icon" name="icon" />
          </span>
          <span v-if="isTextLike && slots.default" :class="textLikeLabelClass"
            ><slot /></span
          >
          <slot v-else />
        </template>
      </button>
    </span>
  </Tooltip>
  <button
    v-else
    v-bind="buttonAttrs"
    :type="buttonType"
    @click="onClick"
    @keydown="onKeydown"
  >
    <template v-if="isIconOnlyButton">
      <Loader v-if="isLoading" :size="LoaderSize.SM" />
      <FontIcon v-else-if="hasIconName" :icon-name="props.iconName!" />
    </template>
    <template v-else-if="isLoading">
      <Loader :size="LoaderSize.SM" />
    </template>
    <template v-else>
      <span v-if="hasIconName || slots.icon" :class="iconClasses">
        <FontIcon v-if="hasIconName" :icon-name="props.iconName!" />
        <slot v-else-if="slots.icon" name="icon" />
      </span>
      <span v-if="isTextLike && slots.default" :class="textLikeLabelClass"
        ><slot /></span
      >
      <slot v-else />
    </template>
  </button>
</template>
